/**
 * @file Region API Service
 * @description API calls and data fetching for region component
 */

import { API_CONFIG } from "./constants.js";
import { fetchData } from "./utils.js";
import { getCachedRegionData, setCachedRegionData } from "./state.js";

/**
 * Fetch region data with caching
 * @param {string} regionID - Region ID (term ID)
 * @returns {Promise<Object>} Region data
 */
export async function fetchRegionData(regionID) {
  // Check cache first
  const cachedData = getCachedRegionData(regionID);
  if (cachedData) {
    return cachedData;
  }

  // Fetch from API
  const apiUrl = `${API_CONFIG.BASE_URL}/${regionID}`;
  const response = await fetchData(apiUrl);

  if (response) {
    setCachedRegionData(regionID, response);
  }

  return response;
}

/**
 * Fetch region data by slug with caching (for map regions)
 * @param {string} regionSlug - Region slug
 * @returns {Promise<Object>} Region data
 */
export async function fetchRegionDataBySlug(regionSlug) {
  // Check cache first
  const cachedData = getCachedRegionData(regionSlug);
  if (cachedData) {
    return cachedData;
  }

  // Fetch from API using slug endpoint
  const apiUrl = `${API_CONFIG.BASE_URL}/?slug=${regionSlug}`;
  const response = await fetchData(apiUrl);

  if (response && response.length > 0) {
    const data = response[0]; // Get first item from array response
    setCachedRegionData(regionSlug, data);
    return data;
  }

  return null;
}

/**
 * Fetch provinces for a region
 * @param {string} regionDataID - Region data ID (term ID)
 * @returns {Promise<Array>} Array of provinces
 */
export async function fetchProvinces(regionDataID) {
  const apiUrl = `${API_CONFIG.BASE_URL}/?parent=${regionDataID}`;
  return await fetchData(apiUrl);
}

/**
 * Fetch region details
 * @param {string} regionDataID - Region data ID (term ID)
 * @returns {Promise<Object>} Region details
 */
export async function fetchRegionDetails(regionDataID) {
  const apiUrl = `${API_CONFIG.BASE_URL}/${regionDataID}`;
  return await fetchData(apiUrl);
}

/**
 * Fetch multiple regions data
 * @param {Array<string>} regionIDs - Array of region IDs
 * @returns {Promise<Array>} Array of region data
 */
export async function fetchMultipleRegions(regionIDs) {
  if (!Array.isArray(regionIDs) || regionIDs.length === 0) {
    return [];
  }

  const promises = regionIDs.map((id) => fetchRegionData(id));
  return await Promise.all(promises);
}

/**
 * Fetch regions by search term
 * @param {string} searchTerm - Search term
 * @param {Object} options - Search options
 * @returns {Promise<Array>} Array of matching regions
 */
export async function searchRegions(searchTerm, options = {}) {
  const params = new URLSearchParams({
    search: searchTerm,
    ...options,
  });

  const apiUrl = `${API_CONFIG.BASE_URL}/?${params.toString()}`;
  return await fetchData(apiUrl);
}

/**
 * Fetch regions by parent
 * @param {string} parentID - Parent region ID
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Array of child regions
 */
export async function fetchChildRegions(parentID, options = {}) {
  const params = new URLSearchParams({
    parent: parentID,
    ...options,
  });

  const apiUrl = `${API_CONFIG.BASE_URL}/?${params.toString()}`;
  return await fetchData(apiUrl);
}

/**
 * Fetch all regions
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Array of all regions
 */
export async function fetchAllRegions(options = {}) {
  const params = new URLSearchParams(options);
  const apiUrl = `${API_CONFIG.BASE_URL}/?${params.toString()}`;
  return await fetchData(apiUrl);
}

/**
 * Preload region data for better performance
 * @param {Array<string>} regionIDs - Array of region IDs to preload
 * @returns {Promise<void>}
 */
export async function preloadRegionData(regionIDs) {
  if (!Array.isArray(regionIDs) || regionIDs.length === 0) {
    return;
  }

  try {
    await fetchMultipleRegions(regionIDs);
    console.debug(`Preloaded ${regionIDs.length} regions`);
  } catch (error) {
    console.error("Error preloading region data:", error);
  }
}

/**
 * Clear API cache
 */
export function clearAPICache() {
  // This will be handled by the state module
  // Just a convenience function for API-specific cache clearing
  console.debug("API cache cleared");
}

/**
 * Get API statistics
 * @returns {Object} API statistics
 */
export function getAPIStats() {
  // This would typically include metrics like:
  // - Number of API calls made
  // - Cache hit rate
  // - Average response time
  // For now, return basic info
  return {
    baseUrl: API_CONFIG.BASE_URL,
    cacheEnabled: true,
    preloadEnabled: true,
  };
}
